import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason?: string) => void;
  title: string;
  description?: string;
  confirmLabel?: string;
  confirmColor?: 'red' | 'blue' | 'green' | 'amber';
  requireReason?: boolean;
  reasonLabel?: string;
  reasonPlaceholder?: string;
  loading?: boolean;
  error?: string | null;
}

const COLORS = {
  red:   { bg: '#dc2626', hover: '#b91c1c' },
  blue:  { bg: '#2563eb', hover: '#1d4ed8' },
  green: { bg: '#16a34a', hover: '#15803d' },
  amber: { bg: '#d97706', hover: '#b45309' },
};

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen, onClose, onConfirm, title, description,
  confirmLabel = 'Confirm', confirmColor = 'blue',
  requireReason = false, reasonLabel = 'Reason (required)',
  reasonPlaceholder = 'Enter reason...',
  loading = false, error = null,
}) => {
  const [reason, setReason] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isOpen) {
      setReason('');
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const canConfirm = !loading && (!requireReason || reason.trim().length > 0);
  const colors = COLORS[confirmColor];

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 100000, backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
    >
      <div
        className="rounded-xl shadow-2xl w-full max-w-md mx-4"
        style={{ backgroundColor: 'var(--card-bg, #fff)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5">
          <h3 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>{title}</h3>
          {description && (
            <p className="text-sm mt-1.5" style={{ color: 'var(--text-secondary)' }}>{description}</p>
          )}

          {requireReason && (
            <div className="mt-4">
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                {reasonLabel}
              </label>
              <textarea
                ref={inputRef}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={reasonPlaceholder}
                rows={2}
                className="w-full px-3 py-2 text-sm rounded-lg border resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
                style={{
                  backgroundColor: 'var(--input-bg, #fff)',
                  borderColor: 'var(--input-border, #d1d5db)',
                  color: 'var(--text-primary)',
                }}
              />
            </div>
          )}

          {error && (
            <div className="mt-3 px-3 py-2 rounded-lg text-xs" style={{ backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}>
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t" style={{ borderColor: 'var(--card-border, #e5e7eb)' }}>
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm rounded-lg transition-colors"
            style={{ color: 'var(--text-secondary)' }}
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(reason.trim() || undefined)}
            disabled={!canConfirm}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-40"
            style={{ backgroundColor: colors.bg }}
            onMouseEnter={(e) => { if (canConfirm) (e.target as HTMLElement).style.backgroundColor = colors.hover; }}
            onMouseLeave={(e) => { (e.target as HTMLElement).style.backgroundColor = colors.bg; }}
          >
            {loading ? 'Processing...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ConfirmationModal;
